/**
 * PublicReviewPage — External client reviews content via a tokenized link (no login).
 * Route: /review/:token
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, XCircle, MessageCircle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/review/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Link ungueltig"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (action) => {
    if (!name.trim()) {
      setErr("Bitte gib deinen Namen ein.");
      return;
    }
    setErr(null);
    setSubmitting(action);
    try {
      await axios.post(`${API}/public/review/${token}/action`, { action, name: name.trim(), note });
      setDone(action);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler beim Senden");
    }
    setSubmitting(null);
  };

  if (loading) return <FullPage><Loader2 className="w-6 h-6 animate-spin" /></FullPage>;
  if (err && !data) {
    return (
      <FullPage>
        <div className="text-center space-y-2" data-testid="review-error">
          <XCircle className="w-10 h-10 text-red-500 mx-auto" />
          <p className="font-heading text-xl">Link ungueltig</p>
          <p className="text-sm text-gray-500">{err}</p>
        </div>
      </FullPage>
    );
  }
  if (!data) return null;

  const { content, company, expires_at } = data;
  const sc = content?.selected_content || {};
  const brandColor = company?.brand_color || "#2563EB";

  if (done) {
    return (
      <FullPage>
        <div className="text-center space-y-3" data-testid="review-success">
          <CheckCircle className="w-12 h-12 mx-auto" style={{ color: brandColor }} />
          <p className="font-heading text-2xl">Danke, {name}!</p>
          <p className="text-sm text-gray-500">
            Deine Entscheidung ({done === "approve" ? "Freigabe" : done === "reject" ? "Ablehnung" : "Kommentar"}) wurde uebermittelt.
          </p>
        </div>
      </FullPage>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1a1a1a] to-[#0F0F0F] py-10 px-4" data-testid="public-review-page">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 text-white">
          {company?.logo_url ? (
            <img
              src={company.logo_url}
              alt={company.name}
              className="w-12 h-12 rounded-lg object-contain bg-white/5 p-1"
              data-testid="review-brand-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <ShieldCheck className="w-7 h-7" style={{ color: brandColor }} />
          )}
          <div>
            <p className="font-heading text-2xl">Externe Freigabe</p>
            <p className="text-xs text-white/60">
              {company?.name || "Content"} &middot; {company?.tagline || `Link gueltig bis ${new Date(expires_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
            </p>
          </div>
        </div>

        {/* Content Preview */}
        <Card className="border-0 shadow-2xl">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Badge className="bg-black text-white">{content?.format_type || content?.type || "Post"}</Badge>
              <p className="text-xs text-gray-500">
                Status: <span className="font-medium">{content?.status}</span>
              </p>
            </div>
            {sc.caption && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Caption</p>
                <p className="text-base bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{sc.caption}</p>
              </div>
            )}
            {sc.hook && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Hook</p>
                <p className="text-sm bg-amber-50 text-amber-900 p-3 rounded-lg">{sc.hook}</p>
              </div>
            )}
            {sc.scenes?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Reel-Szenen</p>
                <div className="space-y-1">
                  {sc.scenes.map((s, i) => (
                    <p key={i} className="text-xs bg-gray-50 p-2 rounded">
                      <b>Szene {s.number}:</b> {s.description}
                      {s.text_overlay && <span className="text-gray-500"> — {s.text_overlay}</span>}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {sc.hashtags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sc.hashtags.map((h, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{h}</Badge>
                ))}
              </div>
            )}
            {content?.schedule_date && (
              <p className="text-xs text-gray-500">Geplant: {content.schedule_date}</p>
            )}
          </CardContent>
        </Card>

        {/* Reviewer Form */}
        <Card className="border-0 shadow-2xl">
          <CardContent className="p-6 space-y-4">
            <p className="font-heading text-lg">Deine Rueckmeldung</p>
            <Input
              placeholder="Dein Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="review-name-input"
            />
            <Textarea
              placeholder="Kommentar oder Begruendung (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="review-note-input"
            />
            {err && <p className="text-xs text-red-500">{err}</p>}
            <div className="grid grid-cols-3 gap-2 pt-2">
              <Button
                onClick={() => submit("reject")}
                disabled={submitting !== null}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="review-reject-btn"
              >
                {submitting === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Ablehnen</>}
              </Button>
              <Button
                onClick={() => submit("comment")}
                disabled={submitting !== null}
                variant="outline"
                data-testid="review-comment-btn"
              >
                {submitting === "comment" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageCircle className="w-4 h-4 mr-1" /> Kommentar</>}
              </Button>
              <Button
                onClick={() => submit("approve")}
                disabled={submitting !== null}
                className="text-white hover:opacity-90"
                style={{ backgroundColor: brandColor }}
                data-testid="review-approve-btn"
              >
                {submitting === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Freigeben</>}
              </Button>
            </div>
            <p className="text-[10px] text-gray-400 text-center pt-1">
              Sicherer, zeitlich begrenzter Review-Link &middot; keine Anmeldung erforderlich
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center p-6 text-white">
      {children}
    </div>
  );
}
